// Sprint-008 A3 (design-desktop-phase-a.md §3.6) — client-side CSV export
// for the desktop data-table pattern. No new backend endpoint: every
// export is built entirely from data already fetched by an existing hook
// (`DataTable`'s caller passes the currently filtered + sorted rows).
//
// Security: guards against CSV/formula injection (OWASP "CSV Injection" —
// a cell opened by Excel/Sheets/Numbers whose content starts with a
// formula-trigger character can execute as a formula, not plain text, in
// the receiving spreadsheet app). Every cell value that starts with `=`,
// `+`, `-`, `@`, a tab, or a carriage return gets a leading single-quote
// prefix, which every major spreadsheet app renders as "force this cell
// to plain text" and strips from the DISPLAYED value, while making the
// value inert as a formula. This is the standard OWASP-recommended
// mitigation (see OWASP "CSV Injection" cheat sheet) — applied uniformly
// to every column (including header labels) rather than only to columns
// judged "risky," since the risk is in the raw cell bytes, not in which
// column they happen to render in.

const FORMULA_TRIGGER_CHARS = ["=", "+", "-", "@", "\t", "\r"];

/** `true` if `raw` starts with a character a spreadsheet app could
 * interpret as "this cell is a formula, not literal text." */
export function isFormulaInjectionRisk(raw: string): boolean {
  return FORMULA_TRIGGER_CHARS.some((prefix) => raw.startsWith(prefix));
}

/** OWASP CSV-injection mitigation: prefix a leading `'` on any cell whose
 * raw text starts with a formula-trigger character. Idempotent — a value
 * that's already safe is returned unchanged. */
export function sanitizeCsvCell(raw: string): string {
  return isFormulaInjectionRisk(raw) ? `'${raw}` : raw;
}

/** RFC 4180 field escaping, applied AFTER the formula-injection guard
 * above (so a sanitized `'=1+1` is what gets checked for embedded commas/
 * quotes/newlines, not the original unsafe string). */
function escapeCsvField(raw: string): string {
  const sanitized = sanitizeCsvCell(raw);
  if (/[",\r\n]/.test(sanitized)) {
    return `"${sanitized.replace(/"/g, '""')}"`;
  }
  return sanitized;
}

export interface CsvColumn<Row> {
  label: string;
  getValue: (row: Row) => string;
}

/** Builds RFC 4180 CSV text (CRLF line endings) from a set of columns and
 * rows — no BOM, no trailing newline (callers that need the BOM for
 * Excel, e.g. `downloadCsv` below, add it at the encoding boundary, not
 * here, so this function's output is plain testable text). */
export function buildCsv<Row>(columns: Array<CsvColumn<Row>>, rows: Row[]): string {
  const header = columns.map((column) => escapeCsvField(column.label)).join(",");
  const body = rows.map((row) =>
    columns.map((column) => escapeCsvField(column.getValue(row))).join(","),
  );
  return [header, ...body].join("\r\n");
}

/** `hayloft-{screen}-{YYYY-MM-DD}.csv` (design-desktop-phase-a.md §3.6).
 * `date` defaults to "now" but is injectable for deterministic tests. */
export function buildCsvFilename(screen: string, date: Date = new Date()): string {
  const iso = date.toISOString().slice(0, 10);
  return `hayloft-${screen}-${iso}.csv`;
}

/** UTF-8 byte-order mark — without it, Excel (Windows in particular)
 * mis-detects plain UTF-8 CSV as the system codepage and mangles any
 * non-ASCII character. Sheets/Numbers/modern Excel all tolerate a
 * leading BOM on UTF-8 CSV without issue. */
const UTF8_BOM = "\uFEFF";

/** Triggers a browser download of `csvContent` as `filename` via a
 * synthetic `Blob` + `<a download>` click — no new backend endpoint. */
export function downloadCsv(filename: string, csvContent: string): void {
  const blob = new Blob([UTF8_BOM + csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
